import { UserData } from '../types/user';
import { saveUserData } from './firebase/userService';
import { Language } from '../types/language';
import { AppError } from './errorHandling';
import { CostBreakdown } from '../types/renovation';
import { validateEmail } from './validation/emailValidator';

interface SaveUserDataParams {
  userData: UserData;
  breakdown: CostBreakdown[];
  totalCost: number;
  language: Language;
}

export class DataService {
  static async saveUserData({ userData, breakdown, totalCost, language }: SaveUserDataParams): Promise<void> {
    try {
      if (!userData.email?.trim() || !validateEmail(userData.email.trim())) {
        throw new AppError(
          'Invalid email',
          'VALIDATION_ERROR',
          {
            en: 'Please provide a valid email address',
            fr: 'Veuillez fournir une adresse email valide'
          }
        );
      }

      if (!breakdown?.length) {
        throw new AppError(
          'No services selected',
          'VALIDATION_ERROR',
          {
            en: 'Please select at least one service',
            fr: 'Veuillez sélectionner au moins un service'
          }
        );
      }

      if (typeof totalCost !== 'number' || totalCost <= 0) {
        throw new AppError(
          'Invalid total cost',
          'VALIDATION_ERROR',
          {
            en: 'Invalid total cost calculation',
            fr: 'Calcul du coût total invalide'
          }
        );
      }

      const enrichedUserData: UserData = {
        ...userData,
        email: userData.email.trim(),
        totalCost,
        services: breakdown.map(item => ({
          serviceName: language === 'fr' ? item.categoryFr : item.category,
          area: Number(item.details.area.toFixed(2)),
          cost: Number(item.cost.toFixed(2))
        }))
      };

      await saveUserData(enrichedUserData, breakdown, language);
    } catch (error) {
      console.error('Error in DataService:', error);
      
      if (error instanceof AppError) {
        throw error;
      }

      throw new AppError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        'UNKNOWN_ERROR',
        {
          en: 'An unexpected error occurred. Please try again.',
          fr: 'Une erreur inattendue s\'est produite. Veuillez réessayer.'
        }
      );
    }
  }
}